import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import feApi from 'api/feApi';
import AppRoute from 'general/constants/AppRoute';
import ToastHelper from 'general/helpers/ToastHelper';
import Utils from 'general/utils/Utils';
import './CustomerProductDetailScreen.scss';

const getCustomerCartStorageKey = (user) => {
  const userKey = user?.userId || user?.userCode || user?.email;
  return userKey ? `evg_customer_cart_${userKey}` : null;
};

const getSavedCartItems = (storageKey) => {
  if (!storageKey) return [];
  try {
    const savedCart = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(savedCart) ? savedCart.filter(item => item?.product?.id != null) : [];
  } catch (error) {
    return [];
  }
};

const formatCurrency = (value) => `${Utils.formatNumber(Math.round(Number(value || 0)))}đ`;
const hasHtmlMarkup = (value) => /<\/?[a-z][\s\S]*>/i.test(String(value || ''));

function CustomerProductDetailScreen({ propProductId, initialProduct, onBack, onCartChange }) {
  const params = useParams();
  const productId = propProductId || params.productId;
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  const [product, setProduct] = useState(initialProduct || null);
  const [loading, setLoading] = useState(!initialProduct);
  const [quantity, setQuantity] = useState(1);
  const [imageIndex, setImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [popupZoomScale, setPopupZoomScale] = useState(1);
  const [popupPan, setPopupPan] = useState({ x: 50, y: 50 });

  const cartStorageKey = useMemo(() => getCustomerCartStorageKey(user), [user?.userId, user?.userCode, user?.email]);

  useEffect(() => {
    if (!initialProduct) {
      if (!productId) {
        ToastHelper.showError('Không tìm thấy sản phẩm');
        navigate(AppRoute.customerHome);
        return;
      }
      setLoading(true);
      setProduct(null);
      feApi.getProducts({ page: 0, limit: 100 })
        .then((response) => {
          const pList = Array.isArray(response?.products) ? response.products : [];
          const rawProduct = pList.find(p => String(p.id) === String(productId) || String(p.categoryId) === String(productId) || p.slug === String(productId));
          if (rawProduct) {
            let specs = rawProduct?.technicalSpecs || {};
            if (typeof specs === 'string') {
              try { specs = JSON.parse(specs); } catch (e) { specs = {}; }
            }
            let images = rawProduct?.images;
            if (typeof images === 'string') {
              try { images = JSON.parse(images); } catch (e) { images = [images.replace(/^"+|"+$/g, '')]; }
            }
            if (!Array.isArray(images)) images = [images];
            const cleanImages = images.map(img => {
              const p = img && typeof img === 'object' ? (img.url || img.path) : img;
              return p ? (String(p).replace(/^"+|"+$/g, '').startsWith('http') ? String(p).replace(/^"+|"+$/g, '') : Utils.getFullUrl(String(p).replace(/^"+|"+$/g, ''))) : '';
            }).filter(Boolean);

            const mapped = {
              ...rawProduct,
              id: rawProduct.id || rawProduct.categoryId,
              code: specs.productCode || rawProduct.categoryCode || `SP${rawProduct.id}`,
              name: rawProduct.name || '',
              price: rawProduct.price ? `${Utils.formatNumber(rawProduct.price)}đ` : 'Liên hệ',
              rawPrice: rawProduct.price || 0,
              originalPrice: rawProduct.originalPrice ? `${Utils.formatNumber(rawProduct.originalPrice)}đ` : '',
              rawOriginalPrice: rawProduct.originalPrice || 0,
              image: cleanImages[0] || '',
              images: cleanImages,
              description: rawProduct.description || '',
              shortDescription: specs.shortDescription || '',
              productCategory: rawProduct.productCategory?.name || '',
              productAttributes: Array.isArray(rawProduct.productAttributes) ? rawProduct.productAttributes.map(a => ({ label: a.label, value: a.value })) : []
            };
            setProduct(mapped);
          } else {
            ToastHelper.showError('Không tìm thấy sản phẩm');
            navigate(AppRoute.customerHome);
          }
        })
        .catch(() => {
          ToastHelper.showError('Lỗi tải dữ liệu sản phẩm');
          navigate(AppRoute.customerHome);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [productId, initialProduct, navigate]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setIsZoomed(false);
        setPopupZoomScale(1);
      }
    };

    if (isZoomed) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleEscape);
    } else {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    }
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isZoomed]);

  const handleAddToCart = (isBuyNow = false) => {
    if (!product) return;
    if (!cartStorageKey) {
      ToastHelper.showError('Lỗi xác thực giỏ hàng');
      return;
    }

    const currentCart = getSavedCartItems(cartStorageKey);
    const existingIndex = currentCart.findIndex(item => item.product?.id === product.id);
    
    if (existingIndex >= 0) {
      currentCart[existingIndex].quantity = Number(currentCart[existingIndex].quantity || 1) + Number(quantity);
    } else {
      currentCart.unshift({
        product: product,
        quantity: quantity,
        checked: true
      });
    }
    
    localStorage.setItem(cartStorageKey, JSON.stringify(currentCart));
    if (onCartChange) onCartChange();
    ToastHelper.showSuccess('Đã thêm sản phẩm vào giỏ hàng');
    
    if (isBuyNow) {
      navigate('/customer/commerce');
    }
  };

  if (loading && !product) {
    return (
      <div className="customer-product-detail-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: 32, color: '#3b82f6' }} />
      </div>
    );
  }

  if (!product) return null;

  const rawPrice = Number(product.rawPrice || 0);
  const rawOriginalPrice = Number(product.rawOriginalPrice || product.originalPrice || 0);
  const discount = rawOriginalPrice > rawPrice ? Math.round((1 - rawPrice / rawOriginalPrice) * 100) : 0;

  return (
    <div className="customer-product-detail-page">
      {/* Fullscreen Zoom Overlay */}
      {isZoomed && product && createPortal(
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9999,
          background: 'rgba(255,255,255,0.98)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <button
            type="button"
            style={{
              position: 'absolute',
              top: 30,
              right: 30,
              border: 0,
              background: 'transparent',
              color: '#0f172a',
              fontSize: 40,
              cursor: 'pointer',
              zIndex: 10000
            }}
            onClick={(e) => {
              e.stopPropagation();
              setIsZoomed(false);
              setPopupZoomScale(1);
            }}
          >
            ×
          </button>
          
          <div style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '40px 20px', boxSizing: 'border-box', position: 'relative' }}>
            {product.images?.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageIndex((curr) => (curr - 1 + product.images.length) % product.images.length);
                    setPopupZoomScale(1);
                  }}
                  style={{
                    position: 'absolute',
                    left: 40,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    display: popupZoomScale === 1 ? 'flex' : 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 10001
                  }}
                >
                  <i className="fa-solid fa-chevron-left" />
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageIndex((curr) => (curr + 1) % product.images.length);
                    setPopupZoomScale(1);
                  }}
                  style={{
                    position: 'absolute',
                    right: 40,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: '#fff',
                    border: '1px solid #e2e8f0',
                    display: popupZoomScale === 1 ? 'flex' : 'none',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    cursor: 'pointer',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 10001
                  }}
                >
                  <i className="fa-solid fa-chevron-right" />
                </button>
              </>
            )}
            <div
              style={{ flex: 1, width: '100%', height: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onMouseMove={(e) => {
                if (popupZoomScale > 1) {
                  const { left, top, width, height } = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - left) / width) * 100;
                  const y = ((e.clientY - top) / height) * 100;
                  setPopupPan({ x, y });
                }
              }}
            >
              <img
                key={`popup-img-${imageIndex}`}
                src={product.images?.[imageIndex] || product.image}
                alt={product.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  transform: `scale(${popupZoomScale})`,
                  transformOrigin: `${popupPan.x}% ${popupPan.y}%`,
                  transition: 'transform 0.3s cubic-bezier(0.2, 0, 0.2, 1)',
                  cursor: popupZoomScale === 1 ? 'zoom-in' : 'zoom-out'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setPopupZoomScale((s) => (s === 1 ? 4 : 1));
                }}
              />
            </div>
          </div>

          {product.images?.length > 1 && (
            <div style={{ display: 'flex', gap: 2, paddingBottom: 40, maxWidth: '100%', overflowX: 'auto', justifyContent: 'center', flexShrink: 0, zIndex: 10002, position: 'relative' }}>
              {product.images.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageIndex(idx);
                    setPopupZoomScale(1);
                  }}
                  style={{
                    width: 64,
                    height: 64,
                    border: 'none',
                    borderBottom: imageIndex === idx ? '4px solid #000' : '4px solid transparent',
                    borderRadius: 0,
                    padding: 0,
                    background: '#f1f5f9',
                    cursor: 'pointer',
                    outline: 'none',
                    boxShadow: 'none',
                    flexShrink: 0,
                    transition: 'none'
                  }}
                >
                  <img src={img} alt="Thumb" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
                </button>
              ))}
            </div>
          )}
        </div>,
        document.body
      )}

      <div className="content-wrapper" style={{ padding: 0 }}>
        <div className="product-container responsive-container">
          <div style={{ marginBottom: 20 }}>
            <button
              type="button"
              className="btn btn-light d-flex align-items-center"
              style={{ 
                gap: 8, 
                padding: '8px 16px', 
                borderRadius: 8, 
                fontWeight: 600, 
                color: '#475569',
                border: '1px solid #e2e8f0',
                background: '#fff',
                transition: 'all 0.2s',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}
              onClick={() => {
                if (onBack) {
                  onBack();
                } else {
                  navigate(-1);
                }
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
              <i className="fa-solid fa-arrow-left" />
              Quay lại
            </button>
          </div>
          {/* TOP SECTION: Gallery & Summary */}
          <div className="responsive-row">
            {/* Left: Product Gallery */}
            <div className="responsive-col" style={{ gap: 16 }}>
              <div style={styles.modalVisual}>
                {(product.images?.[imageIndex] || product.image) && (
                  <img
                    key={`${product.id}-${imageIndex}`}
                    src={product.images?.[imageIndex] || product.image}
                    alt={product.name}
                    style={{ ...styles.modalImage, cursor: 'zoom-in' }}
                    onClick={() => {
                      setIsZoomed(true);
                      setPopupZoomScale(1);
                    }}
                  />
                )}
                {!product.image && (
                  <div style={styles.productFallback}>
                    <div style={styles.modalProductMark}>EVG</div>
                    <div style={{ ...styles.chargerShape, width: 96, height: 168 }} />
                  </div>
                )}
                {product.images?.length > 1 && (
                  <>
                    <button type="button" style={{ ...styles.galleryArrow, left: 12 }} onClick={() => setImageIndex((current) => (current - 1 + product.images.length) % product.images.length)}><i className="fa-solid fa-chevron-left" /></button>
                    <button type="button" style={{ ...styles.galleryArrow, right: 12 }} onClick={() => setImageIndex((current) => (current + 1) % product.images.length)}><i className="fa-solid fa-chevron-right" /></button>
                  </>
                )}
              </div>
              {product.images?.length > 1 && (
                <div style={styles.galleryThumbnails}>
                  {product.images.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      style={{ ...styles.galleryThumbnail, borderColor: imageIndex === index ? '#22c55e' : 'transparent', background: imageIndex === index ? '#f0fdf4' : '#f8fafc', transition: 'none' }}
                      onClick={() => setImageIndex(index)}
                    >
                      <img src={image} alt={`${product.name} ${index + 1}`} style={styles.galleryThumbnailImage} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Product details summary */}
            <div className="responsive-col">
              {/* Badge & Title */}
              <h1 style={{ fontSize: 32, fontWeight: 500, color: '#0f172a', lineHeight: 1.3, margin: '0 0 10px' }}>
                <span style={{ background: '#f59e0b', color: '#fff', fontSize: 14, fontWeight: 500, padding: '4px 10px', borderRadius: 4, marginRight: 12, verticalAlign: 'middle', position: 'relative', top: '-3px' }}>
                  YÊU THÍCH+
                </span>
                <span style={{ verticalAlign: 'middle' }}>{product.name}</span>
              </h1>

              {/* Short Description */}
              <div style={{ marginTop: 12, marginBottom: 20 }}>
                {product.shortDescription ? (
                  <div 
                    className="short-desc-html"
                    style={{ color: '#475569', fontSize: 16, lineHeight: 1.8 }}
                    dangerouslySetInnerHTML={{ __html: product.shortDescription }}
                  />
                ) : (
                  <>
                    {(product.productCategory || product.shortName) && (
                      <div className="short-desc-html" style={{ color: '#475569', fontSize: 16, fontWeight: 500, marginBottom: 10 }}>
                        {product.productCategory || product.shortName}
                      </div>
                    )}
                    {product.productAttributes?.some((attribute) => attribute?.value) && (
                      <ul className="short-desc-html" style={{ color: '#0f172a', fontSize: 16, paddingLeft: 20, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {product.productAttributes
                          .filter((attribute) => attribute?.value)
                          .slice(0, 2)
                          .map((attribute, attributeIndex) => (
                            <li key={`${attribute.label || 'attr'}-${attributeIndex}`}>{attribute.value}</li>
                          ))}
                      </ul>
                    )}
                  </>
                )}
              </div>

              {/* Price Section */}
              <div style={styles.shopeePriceBlock}>
                {rawOriginalPrice > 0 && (
                  <span style={styles.shopeeOriginalPrice}>
                    {product.originalPrice || formatCurrency(rawOriginalPrice)}
                  </span>
                )}
                <span style={styles.shopeeCurrentPrice}>
                  {product.price || formatCurrency(rawPrice)}
                </span>
                {discount > 0 && (
                  <span style={styles.shopeeDiscountBadge}>-{discount}% GIẢM</span>
                )}
              </div>

              {/* Quantity selector */}
              <div style={styles.shopeeQtyLine}>
                <span style={styles.shopeeLabel}>Số lượng</span>
                <div style={styles.shopeeQtyControl}>
                  <button type="button" onClick={() => setQuantity((q) => Math.max(1, q - 1))} style={styles.shopeeQtyButton}>−</button>
                  <input 
                    className="square-qty-input"
                    type="number" 
                    value={quantity} 
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1) {
                        setQuantity(Math.min(100, val));
                      } else if (e.target.value === '') {
                        setQuantity('');
                      }
                    }}
                    onBlur={() => {
                      if (quantity === '' || quantity < 1) {
                        setQuantity(1);
                      }
                    }}
                    style={{ ...styles.shopeeQtyVal, border: 'none', outline: 'none', background: 'transparent', width: 60 }} 
                  />
                  <button type="button" onClick={() => setQuantity((q) => Math.min(100, Number(q || 0) + 1))} style={styles.shopeeQtyButton}>+</button>
                </div>
                <span style={styles.shopeeStockText}>Còn hàng</span>
              </div>

              {/* Buy actions */}
              <div data-shopee-actions className="responsive-actions">
                <button
                  type="button"
                  style={styles.shopeeAddCartBtn}
                  onClick={() => handleAddToCart(false)}
                >
                  <i className="fa-solid fa-cart-plus" /> Thêm vào giỏ hàng
                </button>
                <button
                  type="button"
                  style={styles.shopeeBuyNowBtn}
                  onClick={() => handleAddToCart(true)}
                >
                  Mua sắm ngay
                </button>
              </div>
            </div>
          </div>

          {/* BOTTOM SECTION: Full width Specs & Description */}
          <div style={{ marginTop: 60, paddingTop: 40, borderTop: '1px solid #e2e8f0', width: '100%' }}>
            
            {/* Specs Grid */}
            <div style={{ marginBottom: 40 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 20, textTransform: 'uppercase' }}>Thông số kỹ thuật</h3>
              <div className="responsive-grid">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ color: '#64748b', fontSize: 14, fontWeight: 500 }}>Mã sản phẩm</span>
                  <span style={{ color: '#0f172a', fontSize: 14, fontWeight: 500 }}>{product.code || `SP${product.id}`}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{ color: '#64748b', fontSize: 14, fontWeight: 500 }}>Danh mục</span>
                  <span style={{ color: '#0f172a', fontSize: 14, fontWeight: 500 }}>{product.productCategory || 'Thiết bị sạc'}</span>
                </div>
                {(product.productAttributes || []).map((attr, idx) => (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ color: '#64748b', fontSize: 14, fontWeight: 500 }}>{attr.label}</span>
                    <span style={{ color: '#0f172a', fontSize: 14, fontWeight: 500 }}>{attr.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: 30 }}></div>

            {/* Description */}
            <div style={{ marginBottom: 40 }}>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', marginBottom: 20, textTransform: 'uppercase' }}>Mô tả sản phẩm</h3>
              <div style={{ 
                 color: '#475569', 
                 fontSize: 14, 
                 lineHeight: 1.8, 
                 whiteSpace: 'normal',
                 wordBreak: 'break-word'
              }}
              >
                {hasHtmlMarkup(product.description) ? (
                  <div dangerouslySetInnerHTML={{ __html: product.description }} />
                ) : (
                  product.description || product.note || 'Chưa có mô tả chi tiết cho sản phẩm này.'
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerProductDetailScreen;

const styles = {
  modalContent: { display: 'flex', flexDirection: 'row', width: '100%', gap: 40, alignItems: 'flex-start' },
  productGallery: { width: '50%', display: 'flex', flexDirection: 'column', gap: 16 },
  modalVisual: { width: '100%', aspectRatio: '1', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative', border: 'none' },
  modalImage: { width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' },
  galleryArrow: { position: 'absolute', top: '50%', transform: 'translateY(-50%)', width: 32, height: 32, borderRadius: 16, background: 'rgba(255,255,255,0.9)', color: '#0f172a', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', fontSize: 13, zIndex: 2 },
  galleryThumbnails: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  galleryThumbnail: { width: 64, height: 64, borderRadius: 8, border: '2px solid transparent', padding: 4, cursor: 'pointer', flexShrink: 0 },
  galleryThumbnailImage: { width: '100%', height: '100%', objectFit: 'contain', mixBlendMode: 'multiply' },
  productFallback: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, opacity: 0.5 },
  productFallbackHidden: { display: 'none' },
  modalProductMark: { fontSize: 24, fontWeight: 800, color: '#94a3b8', letterSpacing: 2 },
  chargerShape: { width: 64, height: 110, border: '4px solid #cbd5e1', borderRadius: 16, borderTopRightRadius: 24, position: 'relative' },
  modalSummary: { width: '50%', display: 'flex', flexDirection: 'column' },
  shopeeStats: { display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 16px', fontSize: 14 },
  shopeeRating: { color: '#f59e0b', fontWeight: 500 },
  shopeeDivider: { color: '#e2e8f0' },
  shopeeStatItem: { color: '#64748b' },
  shopeePriceBlock: { background: '#fafafa', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  shopeeOriginalPrice: { color: '#94a3b8', textDecoration: 'line-through', fontSize: 16, whiteSpace: 'nowrap' },
  shopeeCurrentPrice: { color: '#ef4444', fontSize: 32, fontWeight: 500, whiteSpace: 'nowrap' },
  shopeeDiscountBadge: { background: '#fef2f2', color: '#ef4444', fontSize: 11, fontWeight: 500, padding: '2px 6px', borderRadius: 4, textTransform: 'uppercase', whiteSpace: 'nowrap' },
  shopeeQtyLine: { display: 'flex', alignItems: 'center', gap: 24, margin: '24px 0', flexWrap: 'wrap' },
  shopeeLabel: { color: '#64748b', flexShrink: 0, fontWeight: 500 },
  shopeeQtyControl: { display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: 0, overflow: 'hidden', height: 36 },
  shopeeQtyButton: { border: 0, background: '#f1f5f9', width: 36, height: '100%', fontSize: 18, color: '#475569', cursor: 'pointer', outline: 'none', borderRadius: 0 },
  shopeeQtyVal: { width: 50, textAlign: 'center', fontWeight: 500, fontSize: 15, color: '#1e293b', border: 0, outline: 'none', borderRadius: 0 },
  shopeeStockText: { color: '#64748b', fontSize: 14 },
  shopeeActions: { display: 'flex', gap: 24, marginTop: 24, width: '100%', margin: '24px 0 0 0', padding: 0, justifyContent: 'flex-start' },
  shopeeAddCartBtn: { flex: 1, minHeight: 64, background: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)', color: '#15803d', border: '1px solid #22c55e', borderRadius: 14, fontWeight: 500, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 10px 22px rgba(34, 197, 94, .08)' },
  shopeeBuyNowBtn: { flex: 1, minHeight: 64, background: 'linear-gradient(180deg, #22c55e 0%, #16a34a 100%)', color: '#fff', border: 0, borderRadius: 14, fontWeight: 500, fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 10px 22px rgba(34, 197, 94, .18)' },
  shopeeSpecs: { display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24, marginBottom: 24 },
  shopeeSpecItem: { display: 'flex', gap: 16, alignItems: 'flex-start', fontSize: 14 },
  shopeeValue: { color: '#1e293b', fontWeight: 500, whiteSpace: 'pre-line', wordBreak: 'break-word', flex: 1 },
  shopeeDesc: { color: '#475569', fontSize: 14, lineHeight: 1.6, marginTop: 8, wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
};
